/**
 * TypingIndicator — WhatsApp-style chat bubble with pulsing dots
 *
 * Shows as an incoming message bubble with the first typing user's avatar.
 */

import { memo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChatAvatar } from './ChatAvatar';
import { chatHaptic } from '../utils/chatHaptic';

interface TypingIndicatorProps {
  typingUsers: Array<{ userId: string; userName: string; userAvatar?: string }>;
  className?: string;
}

export const TypingIndicator = memo(function TypingIndicator({
  typingUsers,
  className = '',
}: TypingIndicatorProps) {
  const firstUser = typingUsers[0] ?? null;
  const prevCountRef = useRef(0);

  // Fire haptic once when typing users transition from 0 → 1+
  useEffect(() => {
    if (prevCountRef.current === 0 && typingUsers.length > 0) {
      chatHaptic.typing();
    }
    prevCountRef.current = typingUsers.length;
  }, [typingUsers.length]);

  return (
    <AnimatePresence>
      {firstUser && (
        <motion.div
          key="typing-bubble"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.2 }}
          className={`flex items-end justify-start px-4 py-1 ${className}`}
        >
          <div className="me-2 flex-shrink-0">
            <ChatAvatar
              src={firstUser.userAvatar}
              name={firstUser.userName || '?'}
              size="h-8 w-8"
            />
          </div>
          <div className="rounded-2xl border border-white/20 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-md backdrop-saturate-150">
            <div className="flex items-center gap-[5px]">
              <span className="typing-dot h-[7px] w-[7px] rounded-full bg-neutral-400" style={{ animationDelay: '0ms' }} />
              <span className="typing-dot h-[7px] w-[7px] rounded-full bg-neutral-400" style={{ animationDelay: '150ms' }} />
              <span className="typing-dot h-[7px] w-[7px] rounded-full bg-neutral-400" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default TypingIndicator;
